import { useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CancelIcon from "@mui/icons-material/Cancel";
import colors from "../config/colors";
import { MARKETPLACE_BRAND_NAME_PLATFORMS } from "../utils/trackingConstants";

const STATUS_CHIPS = {
  verified: {
    label: "Verified",
    icon: CheckCircleIcon,
    color: "#0E7A4A",
    bgcolor: "#E3F6EC",
  },
  pending_review: {
    label: "Pending verification",
    icon: ScheduleIcon,
    color: "#A88400",
    bgcolor: "#FFF6CC",
    tooltip: "Usually takes 3-5 business days",
  },
  rejected: {
    label: "Rejected",
    icon: CancelIcon,
    color: colors.red700,
    bgcolor: colors.red100,
  },
};

const inputSx = {
  height: 40,
  border: `1px solid ${colors.gray100}`,
  borderRadius: "8px",
  px: 1.5,
  fontSize: 14,
  fontFamily: "inherit",
  color: "#0D0D0D",
  outline: "none",
  bgcolor: colors.white,
  minWidth: 0,
  "&::placeholder": { color: colors.gray200 },
  "&:focus": { borderColor: colors.gray600 },
};

export const marketplaceLabel = (platformId) =>
  MARKETPLACE_BRAND_NAME_PLATFORMS.find((p) => p.id === platformId)?.label ||
  "";

export function MarketplaceBrandNameForm({ onAdd, isPending = false }) {
  const [name, setName] = useState("");
  const [platformId, setPlatformId] = useState("");
  const trimmedName = name.trim();

  const submit = async () => {
    const added = await onAdd({ platformId, name: trimmedName });
    if (added !== false) {
      setName("");
      setPlatformId("");
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Box
        component="input"
        placeholder="Enter brand name"
        value={name}
        maxLength={255}
        onChange={(e) => setName(e.target.value)}
        sx={{ ...inputSx, flex: 1 }}
      />
      <Select
        value={platformId}
        displayEmpty
        onChange={(e) => setPlatformId(e.target.value)}
        renderValue={(value) => (
          <Typography
            sx={{ fontSize: 14, color: value ? "#0D0D0D" : colors.gray200 }}
          >
            {value ? marketplaceLabel(value) : "Select marketplace"}
          </Typography>
        )}
        sx={{
          flex: 1,
          height: 40,
          borderRadius: "8px",
          ".MuiOutlinedInput-notchedOutline": { borderColor: colors.gray100 },
        }}
      >
        {MARKETPLACE_BRAND_NAME_PLATFORMS.map((p) => (
          <MenuItem key={p.id} value={p.id} sx={{ fontSize: 14 }}>
            {p.label}
          </MenuItem>
        ))}
      </Select>
      <Button
        onClick={submit}
        disabled={!trimmedName || !platformId || isPending}
        sx={{
          bgcolor: colors.primary500,
          color: colors.white,
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "8px",
          px: 2.5,
          "&:hover": { bgcolor: "#D44330" },
          "&.Mui-disabled": { bgcolor: "#F5A89E", color: colors.white },
        }}
      >
        Add
      </Button>
    </Box>
  );
}

export function MarketplaceStatusChip({ status }) {
  const chip = STATUS_CHIPS[status];
  if (!chip) return null;
  const Icon = chip.icon;
  return (
    <Tooltip title={chip.tooltip || ""} arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          px: 0.75,
          py: "2px",
          borderRadius: "4px",
          bgcolor: chip.bgcolor,
          color: chip.color,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
          {chip.label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export function MarketplaceBrandNameRow({ name, platformLabel, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        bgcolor: "#EFEFEF",
        borderRadius: "12px",
        px: 1.5,
        py: 1,
        minHeight: 52,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }} title={name}>
        <Typography
          noWrap
          sx={{ fontSize: 14, fontWeight: 500, color: "#0D0D0D" }}
        >
          {name}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: colors.gray200 }}>
          {platformLabel}
        </Typography>
      </Box>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
      >
        {children}
      </Box>
    </Box>
  );
}
